import React, { useEffect, useRef, useState } from 'react';
import { PageHeader, Btn, Card, Empty, Spinner, useToast } from '../../components/shared/UI';
import api from '../../utils/api';

const fmtBytes = b => {
  const u = ['B','KB','MB','GB','TB']; let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 ? 2 : 1)} ${u[i]}`;
};
const fmtDate = d => new Date(d).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' });

const fileToBase64 = file => new Promise((res, rej) => {
  const reader = new FileReader();
  reader.onload = () => {
    // result = "data:...;base64,XXXX" — strip the prefix
    const b64 = String(reader.result).split(',', 2)[1];
    res(b64);
  };
  reader.onerror = rej;
  reader.readAsDataURL(file);
});

export default function AdminBackups() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState('');
  const [deleting, setDeleting]   = useState('');
  const [uploading, setUploading] = useState(false);
  const [r2Configured, setR2Configured] = useState(false);
  const fileInputRef = useRef(null);
  const { show, ToastContainer } = useToast();

  // One merged list: local files (since the last deploy) + the durable R2 history
  const load = () => api.get('/admin/backups').then(r => {
    setBackups(r.data.backups || []);
    setR2Configured(!!r.data.r2Configured);
  }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/admin/backups');
      show(`Backup created (${data.totalDocs} docs)`);
      load();
      // The R2 upload runs in the background on the server — refresh for the badge
      setTimeout(load, 4000);
    } catch (err) {
      show(err.response?.data?.message || 'Backup failed', 'error');
    } finally { setCreating(false); }
  };

  const download = async filename => {
    try {
      const res = await api.get(`/admin/backups/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      show('Download failed', 'error');
    }
  };

  const restore = async filename => {
    if (!window.confirm(`Restore database from "${filename}"?\n\nThis will OVERWRITE all current data with the contents of this backup.`)) return;
    setRestoring(filename);
    try {
      const { data } = await api.post(`/admin/backups/${filename}/restore`);
      show(`Restored ${data.docs} documents. Reloading…`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      show(err.response?.data?.message || 'Restore failed', 'error');
    } finally { setRestoring(''); }
  };

  const del = async filename => {
    if (!window.confirm(`Delete backup "${filename}"?\n\nThis removes it from this server AND from the Cloudflare R2 mirror.`)) return;
    setDeleting(filename);
    try {
      await api.delete(`/admin/backups/${filename}`);
      setBackups(b => b.filter(x => x.filename !== filename));
      show('Backup deleted');
    } catch {
      show('Delete failed', 'error');
    } finally { setDeleting(''); }
  };

  const onUpload = async e => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow same-file re-upload
    if (!file) return;
    if (!window.confirm(`Restore database from "${file.name}"?\n\nThis will OVERWRITE all current data with the contents of this file.`)) return;

    setUploading(true);
    try {
      const dataBase64 = await fileToBase64(file);
      // Sanitize filename so the server can save it to ./backups/
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filename = /\.json\.gz$/.test(safe) ? safe : `uploaded-${Date.now()}.json.gz`;
      const { data } = await api.post('/admin/backups/upload-restore', { filename, dataBase64 });
      show(`Restored ${data.docs} documents from upload. Reloading…`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      show(err.response?.data?.message || err.message || 'Upload-restore failed', 'error');
    } finally { setUploading(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <ToastContainer />
      <input
        ref={fileInputRef}
        type="file"
        accept=".gz,.json,application/gzip,application/json"
        onChange={onUpload}
        style={{ display: 'none' }}
      />
      <PageHeader
        title="Backups"
        subtitle={`${backups.length} backup${backups.length !== 1 ? 's' : ''} stored in ./backups/`}
        actions={
          <>
            <Btn variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? 'Restoring…' : '⬆ Upload & Restore'}
            </Btn>
            <Btn variant="primary" onClick={create} disabled={creating}>
              {creating ? 'Creating…' : '+ Create Backup'}
            </Btn>
          </>
        }
      />

      <Card style={{ marginBottom: 20, background: 'rgba(46,117,182,0.06)', border: '1px solid rgba(46,117,182,0.2)' }}>
        <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>
          Backups are pure-Node JSON snapshots (gzipped) of every collection — no external tools required.
          You can <b>download</b> any backup to your computer and later <b>upload</b> it from any deployment to restore.
          Restoring drops existing collections first, so it's a full replace, not a merge.
        </p>
        <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, marginTop: 8 }}>
          A backup also runs <b>automatically once a day</b>; only the newest 7 automatic ones
          (tagged <b>Auto</b>) are kept. Backups you create here are never auto-deleted.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.6, marginTop: 8, color: r2Configured ? 'var(--success)' : 'var(--danger)' }}>
          {r2Configured
            ? <>☁ Every backup is <b>mirrored to Cloudflare R2</b> (marked ☁ below). The server's own disk is wiped on every deploy, so the R2 copies are the durable history — cloud-only backups can still be downloaded and restored from here. The newest 30 automatic ones are kept in the cloud.</>
            : <>⚠ This server's disk is <b>wiped on every deploy</b>, and Cloudflare R2 mirroring is <b>not configured</b> — so backups do not survive a deploy. Set R2_ACCOUNT_ID, R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY and R2_BUCKET to keep a durable off-server history.</>}
        </p>
      </Card>

      {backups.length === 0
        ? <Empty icon="💾" message="No backups yet. Click Create Backup to make one." />
        : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  <th style={th}>Filename</th>
                  <th style={th}>Size</th>
                  <th style={th}>Created</th>
                  <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b, i) => (
                  <tr key={b.filename} style={{ borderBottom: '1px solid var(--border-light)', background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--hover-bg)' }}>
                    <td style={{ ...td, fontFamily: 'DM Mono, monospace', fontSize: 12 }}>
                      {b.filename}
                      {b.auto && (
                        <span style={{
                          marginLeft: 8, fontSize: 10, fontWeight: 700, fontFamily: 'inherit',
                          background: 'rgba(46,117,182,0.15)', color: 'var(--blue)',
                          borderRadius: 10, padding: '2px 8px', verticalAlign: 'middle',
                        }}>AUTO</span>
                      )}
                      {b.cloud && (
                        <span title={b.local ? 'Mirrored to Cloudflare R2' : 'Only in Cloudflare R2 (local copy was wiped by a deploy) — download and restore still work'} style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 700, fontFamily: 'inherit',
                          background: 'rgba(16,185,129,0.15)', color: 'var(--success)',
                          borderRadius: 10, padding: '2px 8px', verticalAlign: 'middle',
                        }}>☁ R2</span>
                      )}
                      {b.local === false && (
                        <span title="No copy on this server — served from R2" style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 700, fontFamily: 'inherit',
                          background: 'rgba(148,163,184,0.15)', color: 'var(--text-muted)',
                          borderRadius: 10, padding: '2px 8px', verticalAlign: 'middle',
                        }}>cloud only</span>
                      )}
                    </td>
                    <td style={{ ...td, color: 'var(--text-muted)' }}>{fmtBytes(b.sizeBytes)}</td>
                    <td style={{ ...td, color: 'var(--text-muted)' }}>{fmtDate(b.createdAt)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Btn size="sm" variant="ghost" onClick={() => download(b.filename)}>⬇ Download</Btn>
                        <Btn size="sm" variant="outline" onClick={() => restore(b.filename)} disabled={restoring === b.filename}>
                          {restoring === b.filename ? 'Restoring…' : '↺ Restore'}
                        </Btn>
                        <Btn size="sm" variant="danger" onClick={() => del(b.filename)} disabled={deleting === b.filename}>Del</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>
        )
      }
    </div>
  );
}

const th = { padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' };
const td = { padding: '13px 16px', fontSize: 14, color: 'var(--text)', verticalAlign: 'middle' };
